import type {
  SolanaTransactionInput,
  DecodedTransaction,
  DecodedField,
} from '@txviz/core';
import {
  formatValue,
  bytesToHex,
  hexToBytes,
  sanitizeDisplayString,
} from '@txviz/core';
import { serializeSolanaTransaction } from './serializer.js';
import {
  KNOWN_PROGRAMS,
  SYSTEM_INSTRUCTIONS,
  TOKEN_INSTRUCTIONS,
  base58Decode,
} from './types.js';

/**
 * Decode a Solana transaction into human-readable fields.
 */
export function decodeSolanaTransaction(tx: SolanaTransactionInput): DecodedTransaction {
  const fields: DecodedField[] = [];
  const serialized = serializeSolanaTransaction(tx);
  const rawHex = bytesToHex(serialized);

  // Fee Payer
  fields.push({
    label: 'Fee Payer',
    value: tx.feePayer,
    rawValue: tx.feePayer,
    type: 'address',
  });

  // Recent Blockhash
  fields.push({
    label: 'Recent Blockhash',
    value: tx.recentBlockhash,
    rawValue: tx.recentBlockhash,
    type: 'data',
  });

  // Instructions
  fields.push({
    label: 'Instructions',
    value: `${tx.instructions.length} instruction(s)`,
    rawValue: tx.instructions.length.toString(),
    type: 'number',
  });

  for (let i = 0; i < tx.instructions.length; i++) {
    const ix = tx.instructions[i]!;
    const programName = KNOWN_PROGRAMS[ix.programId] ?? ix.programId;

    fields.push({
      label: `Instruction ${i} Program`,
      value: sanitizeDisplayString(programName),
      rawValue: ix.programId,
      type: 'text',
    });

    // Try to decode known instruction types
    const decodedIx = decodeKnownInstruction(ix.programId, ix.data);
    if (decodedIx) {
      fields.push({
        label: `Instruction ${i} Action`,
        value: sanitizeDisplayString(decodedIx.name),
        rawValue: decodedIx.name,
        type: 'text',
      });

      fields.push({
        label: `Instruction ${i} Description`,
        value: sanitizeDisplayString(decodedIx.description),
        rawValue: decodedIx.description,
        type: 'text',
      });

      for (const field of decodedIx.fields) {
        fields.push({
          ...field,
          label: `Instruction ${i} ${field.label}`,
        });
      }
    }

    // Account list — with named roles if available
    if (ix.accounts.length > 0) {
      for (let j = 0; j < ix.accounts.length; j++) {
        const acc = ix.accounts[j]!;
        const flags: string[] = [];
        if (acc.isSigner) flags.push('signer');
        if (acc.isWritable) flags.push('writable');
        const flagStr = flags.length > 0 ? ` (${flags.join(', ')})` : ' (read-only)';

        // Use named label if available from instruction decoding
        const label = decodedIx?.accountLabels?.[j]
          ? `Instruction ${i} ${decodedIx.accountLabels[j]}`
          : `Instruction ${i} Account ${j}`;

        fields.push({
          label,
          value: `${acc.pubkey}${flagStr}`,
          rawValue: acc.pubkey,
          type: 'address',
        });
      }
    }
  }

  // Build summary
  const summary = buildSummary(tx);

  return {
    chain: 'solana',
    type: 'Solana Transaction',
    summary,
    fields,
    rawHex,
  };
}

interface DecodedInstruction {
  name: string;
  description: string;
  fields: DecodedField[];
  /** Named account roles for this instruction type */
  accountLabels?: string[] | undefined;
}

function decodeKnownInstruction(
  programId: string,
  data: string,
): DecodedInstruction | null {
  let dataBytes: Uint8Array;
  try {
    if (data.startsWith('0x')) {
      dataBytes = hexToBytes(data);
    } else if (data.length === 0) {
      dataBytes = new Uint8Array(0);
    } else {
      dataBytes = base58Decode(data);
    }
  } catch {
    return null;
  }

  if (dataBytes.length < 4) return null;

  const view = new DataView(dataBytes.buffer, dataBytes.byteOffset);

  // System Program
  if (programId === '11111111111111111111111111111111') {
    const instructionType = view.getUint32(0, true);
    const name = SYSTEM_INSTRUCTIONS[instructionType];
    if (!name) return null;

    const fields: DecodedField[] = [];
    let description = '';
    let accountLabels: string[] | undefined;

    switch (instructionType) {
      case 0: // CreateAccount
        description = 'Creates a new account with the specified amount of space and SOL balance';
        accountLabels = ['Funding Account', 'New Account'];
        break;
      case 1: // Assign
        description = 'Assigns the account to a new owning program';
        accountLabels = ['Account'];
        break;
      case 2: // Transfer
        description = 'Transfers SOL from one account to another';
        accountLabels = ['From', 'To'];
        if (dataBytes.length >= 12) {
          const lamports = view.getBigUint64(4, true);
          fields.push({
            label: 'Amount',
            value: formatValue(lamports, 'solana'),
            rawValue: lamports.toString(),
            type: 'value',
          });
        }
        break;
      case 4: // AdvanceNonceAccount
        description = 'Advances the nonce account to a new blockhash, enabling durable transactions that do not expire';
        accountLabels = ['Nonce Account', 'RecentBlockhashes Sysvar', 'Nonce Authority'];
        break;
      case 5: // WithdrawNonceAccount
        description = 'Withdraws SOL from a nonce account';
        accountLabels = ['Nonce Account', 'Destination', 'RecentBlockhashes Sysvar', 'Rent Sysvar', 'Nonce Authority'];
        break;
      case 6: // InitializeNonceAccount
        description = 'Initializes a nonce account for durable transactions';
        accountLabels = ['Nonce Account', 'RecentBlockhashes Sysvar', 'Rent Sysvar'];
        break;
      case 8: // Allocate
        description = 'Allocates space in an account without funding it';
        accountLabels = ['Account'];
        break;
      default:
        description = `System Program instruction: ${name}`;
    }

    return { name, description, fields, accountLabels };
  }

  // Token Program
  if (
    programId === 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' ||
    programId === 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb'
  ) {
    const instructionType = dataBytes[0];
    if (instructionType === undefined) return null;
    const name = TOKEN_INSTRUCTIONS[instructionType];
    if (!name) return null;

    const fields: DecodedField[] = [];
    let description = '';
    let accountLabels: string[] | undefined;

    switch (instructionType) {
      case 3: // Transfer
        description = 'Transfers tokens from one token account to another';
        accountLabels = ['Source Token Account', 'Destination Token Account', 'Owner/Authority'];
        if (dataBytes.length >= 9) {
          const amount = view.getBigUint64(1, true);
          fields.push({
            label: 'Amount',
            value: amount.toString(),
            rawValue: amount.toString(),
            type: 'value',
            warning: 'Token amount in raw units — check token decimals',
          });
        }
        break;
      case 4: // Approve
        description = 'Approves a delegate to transfer tokens from this account';
        accountLabels = ['Token Account', 'Delegate', 'Owner'];
        break;
      case 5: // Revoke
        description = 'Revokes a previously approved delegate';
        accountLabels = ['Token Account', 'Owner'];
        break;
      case 7: // MintTo
        description = 'Mints new tokens to a token account';
        accountLabels = ['Mint', 'Destination Token Account', 'Mint Authority'];
        break;
      case 8: // Burn
        description = 'Burns tokens from a token account, reducing supply';
        accountLabels = ['Token Account', 'Mint', 'Owner'];
        break;
      case 9: // CloseAccount
        description = 'Closes a token account and transfers remaining SOL to destination';
        accountLabels = ['Account to Close', 'SOL Destination', 'Owner'];
        break;
      case 12: // TransferChecked
        description = 'Transfers tokens with explicit decimal verification';
        accountLabels = ['Source Token Account', 'Token Mint', 'Destination Token Account', 'Owner/Authority'];
        if (dataBytes.length >= 10) {
          const amount = view.getBigUint64(1, true);
          const decimals = dataBytes[9];
          fields.push({
            label: 'Amount',
            value: amount.toString(),
            rawValue: amount.toString(),
            type: 'value',
          });
          if (decimals !== undefined) {
            fields.push({
              label: 'Decimals',
              value: decimals.toString(),
              rawValue: decimals.toString(),
              type: 'number',
            });
          }
        }
        break;
      default:
        description = `Token Program instruction: ${name}`;
    }

    return { name, description, fields, accountLabels };
  }

  return null;
}

function buildSummary(tx: SolanaTransactionInput): string {
  const parts: string[] = [];

  // Try to build a meaningful description from decoded instructions
  for (const ix of tx.instructions) {
    const decoded = decodeKnownInstruction(ix.programId, ix.data);
    if (decoded) {
      parts.push(decoded.description);
      break;
    }
  }

  if (parts.length === 0) {
    parts.push(`${tx.instructions.length} instruction(s)`);
    for (const ix of tx.instructions) {
      const programName = KNOWN_PROGRAMS[ix.programId];
      if (programName) {
        parts.push(`via ${programName}`);
        break;
      }
    }
  }

  parts.push(`fee payer: ${tx.feePayer}`);

  return sanitizeDisplayString(parts.join(', '));
}
